import React, { useState, useEffect } from 'react';
import { Sparkles, FileText, BarChart3, LayoutDashboard, MessageSquare, Upload, Palette, ShieldCheck, ChevronRight, Menu, X, Wand2, ArrowRight, CheckCircle2, PenTool, Table } from 'lucide-react';

export default function LandingB2C() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans antialiased">
      {/* Nav */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wand2 className="w-6 h-6 text-violet-600" />
            <span className="text-xl font-bold tracking-tight">wizardly.ai</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-gray-600">
            <button onClick={() => scrollTo('features')} className="hover:text-gray-900 transition-colors">Features</button>
            <button onClick={() => scrollTo('how-it-works')} className="hover:text-gray-900 transition-colors">How It Works</button>
            <button onClick={() => scrollTo('use-cases')} className="hover:text-gray-900 transition-colors">Use Cases</button>
            <button onClick={() => scrollTo('faq')} className="hover:text-gray-900 transition-colors">FAQ</button>
            <a href="https://prezlab-brain.netlify.app" className="ml-2 px-5 py-2.5 bg-violet-600 text-white rounded-full text-sm font-medium hover:bg-violet-700 transition-colors">
              Start Free →
            </a>
          </div>
          <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t px-6 py-4 space-y-3">
            <button onClick={() => scrollTo('features')} className="block w-full text-left text-sm text-gray-600 hover:text-gray-900">Features</button>
            <button onClick={() => scrollTo('how-it-works')} className="block w-full text-left text-sm text-gray-600 hover:text-gray-900">How It Works</button>
            <button onClick={() => scrollTo('use-cases')} className="block w-full text-left text-sm text-gray-600 hover:text-gray-900">Use Cases</button>
            <button onClick={() => scrollTo('faq')} className="block w-full text-left text-sm text-gray-600 hover:text-gray-900">FAQ</button>
            <a href="https://prezlab-brain.netlify.app" className="block w-full text-center px-5 py-2.5 bg-violet-600 text-white rounded-full text-sm font-medium">Start Free →</a>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-violet-50 text-violet-700 rounded-full text-sm font-medium mb-8">
            <Sparkles className="w-4 h-4" />
            AI-powered design — no design skills needed
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] mb-6">
            Your ideas deserve better than a{' '}
            <span className="bg-gradient-to-r from-violet-600 to-indigo-600 bg-clip-text text-transparent">blank PowerPoint</span>
          </h1>
          <p className="text-xl text-gray-500 max-w-2xl mx-auto mb-10 leading-relaxed">
            wizardly.ai doesn't just make slides — it thinks through your content, builds a narrative, and designs every page from scratch. Presentations, documents, infographics, dashboards. Describe it, get it.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
            <a href="https://prezlab-brain.netlify.app" className="px-8 py-4 bg-violet-600 text-white rounded-full text-lg font-semibold hover:bg-violet-700 transition-all hover:shadow-lg hover:shadow-violet-200">
              Start Creating — Free
            </a>
            <button onClick={() => scrollTo('demo')} className="px-8 py-4 text-gray-600 rounded-full text-lg font-medium hover:text-gray-900 hover:bg-gray-50 transition-colors flex items-center gap-2">
              See it in action <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-gray-400">No credit card · No templates · Export to PPTX in minutes</p>
        </div>

        {/* Hero product screenshot placeholder */}
        <div className="max-w-5xl mx-auto mt-16">
          <div className="relative rounded-2xl border border-gray-200 bg-gradient-to-b from-gray-50 to-white shadow-2xl shadow-gray-200/50 overflow-hidden">
            <div className="aspect-[16/9] flex items-center justify-center text-gray-300">
              <div className="text-center">
                <LayoutDashboard className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-sm text-gray-400">Product screenshot — AI Consultant generating an outline</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Sound familiar?</h2>
            <p className="text-lg text-gray-500">The work is done. The presentation isn't.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <PainCard
              title="The 11 PM Slide Sprint"
              text="You've done the analysis. You know the story. But translating it into something that looks credible? That's another 3 hours you don't have. So you pick a template, fight the layouts, and ship something you're not proud of."
              emoji="🕐"
            />
            <PainCard
              title="The One-Person Studio"
              text="You're a consultant, a founder, a freelancer. Your ideas compete with agencies that have design teams. You can't hire one — but your deliverables can't look like you didn't."
              emoji="💼"
            />
            <PainCard
              title="The Design Queue"
              text="Your manager wants the deck by Thursday. The design team can fit you in next month. So you build it yourself — mismatched fonts, crooked alignment, that one chart you can't resize. Everyone's seen this deck."
              emoji="⏳"
            />
          </div>
        </div>
      </section>

      {/* Product Demo */}
      <section id="demo" className="py-24 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Watch a deck build itself</h2>
          <p className="text-lg text-gray-500 mb-12">From prompt to presentation in under 4 minutes</p>

          {/* Video placeholder */}
          <div className="relative rounded-2xl border border-gray-200 bg-gray-50 overflow-hidden mb-16">
            <div className="aspect-video flex items-center justify-center">
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-4">
                  <ChevronRight className="w-8 h-8 text-violet-600 ml-1" />
                </div>
                <p className="text-sm text-gray-400">60-second product demo video</p>
              </div>
            </div>
          </div>

          {/* Metric cards */}
          <div className="grid sm:grid-cols-3 gap-6">
            <MetricCard value="5 formats" label="Presentations, documents, infographics, dashboards, spreadsheets" />
            <MetricCard value="~4 min" label="From prompt to downloadable deck" />
            <MetricCard value="0 templates" label="Every output is generated from scratch" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">A design team that works at the speed of conversation</h2>
            <p className="text-lg text-gray-500">Everything a design team delivers. Without the retainer.</p>
          </div>

          <div className="space-y-24">
            <FeatureRow
              number="01"
              title="AI Consultant"
              description="Most AI tools skip straight to generating. wizardly.ai starts by understanding — it asks about your audience, your goal, your constraints. It builds a content strategy before touching a single layout. The result is a presentation with an argument, not just slides with text on them."
              icon={<MessageSquare className="w-7 h-7" />}
              screenshotLabel="AI Consultant chat with strategic plan"
              reverse={false}
            />
            <FeatureRow
              number="02"
              title="Brand Identity"
              description="Upload your colors, fonts, logo, and style rules once. From that point on, everything you generate looks like it came from your brand's design team. Consistency without oversight. Identity without a style guide PDF nobody reads."
              icon={<Palette className="w-7 h-7" />}
              screenshotLabel="Brand panel with branded output"
              reverse={true}
            />
            <FeatureRow
              number="03"
              title="Five Formats, One Conversation"
              description="Need a pitch deck? A one-pager? A dashboard for the board? A report for the client? Tell wizardly.ai what you need — it picks the right format and generates it. Stop rebuilding the same content across PowerPoint, Word, and Excel."
              icon={<FileText className="w-7 h-7" />}
              screenshotLabel="Multi-format output from single prompt"
              reverse={false}
            />
            <FeatureRow
              number="04"
              title="Knowledge Base"
              description="Feed it your company data, past decks, research, and approved figures. The AI references your actual numbers and language — not generic filler. The more context you give it, the less editing you do after."
              icon={<Upload className="w-7 h-7" />}
              screenshotLabel="Knowledge base with slide citing uploaded data"
              reverse={true}
            />
            <FeatureRow
              number="05"
              title="Chat Editing"
              description={`"Make slide 5 more visual." "Add a competitor comparison after the intro." "Tone down the opening — it's too salesy." Edit through conversation, not through drag-and-drop. The AI understands design intent, not just text swaps.`}
              icon={<PenTool className="w-7 h-7" />}
              screenshotLabel="Chat command with before/after slide"
              reverse={false}
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Prompt. Pick. Present.</h2>
            <p className="text-lg text-gray-500">No design degree required. No template hunting.</p>
          </div>

          <div className="space-y-12">
            <StepCard
              step="01"
              title="Tell it what you're building"
              text="Type a prompt, upload a document, or paste raw notes. The AI Consultant asks 2-3 questions to nail down your audience, message, and format — then builds a structured content plan."
            />
            <StepCard
              step="02"
              title="Pick your look"
              text="wizardly.ai generates multiple design directions for the same content. Choose the one that fits your context — or apply your brand identity to lock in your visual system automatically."
            />
            <StepCard
              step="03"
              title="Refine and ship"
              text={`Review the output. Chat to adjust anything — add slides, rewrite sections, change the visual tone. When it's ready, export as PPTX, PDF, or HTML.`}
            />
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section id="use-cases" className="py-24 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Built for people who make decks, not people who love making decks</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <UseCaseCard icon={<Sparkles className="w-5 h-5" />} role="Consultants & Freelancers" desc="Deliver agency-quality proposals without agency overhead" />
            <UseCaseCard icon={<BarChart3 className="w-5 h-5" />} role="Founders" desc="Build investor decks that match the ambition of your idea" />
            <UseCaseCard icon={<FileText className="w-5 h-5" />} role="Marketers" desc="Campaign decks, data reports, and infographics without waiting for creative" />
            <UseCaseCard icon={<Table className="w-5 h-5" />} role="Educators" desc="Turn syllabi, papers, and lesson plans into visual presentations instantly" />
            <UseCaseCard icon={<PenTool className="w-5 h-5" />} role="Students & Researchers" desc="Present complex work clearly without spending the weekend on slides" />
            <UseCaseCard icon={<MessageSquare className="w-5 h-5" />} role="Anyone" desc={`Who's ever spent 3 hours on 10 slides and thought "there has to be a better way"`} />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">Frequently asked questions</h2>
          <div className="space-y-6">
            <FAQItem
              q="Is it actually free?"
              a="Yes. Free plan, no credit card. Use it, evaluate it, decide for yourself."
            />
            <FAQItem
              q="How is this different from Canva / Gamma / Beautiful.ai?"
              a="Those tools arrange text on templates. wizardly.ai analyzes your content, plans a narrative structure, and generates original layouts — no template library, no drag-and-drop. The AI Consultant plans your deliverable the way a strategist would."
            />
            <FAQItem
              q="Will it look professional enough to present?"
              a="The AI generates production-quality layouts with proper typography, spacing, and hierarchy. Add your brand identity and it's indistinguishable from designer-made work."
            />
            <FAQItem
              q="Is my content private?"
              a="Processed to generate your output, never used for model training. Delete anytime."
            />
            <FAQItem
              q="Can I edit the output?"
              a="Yes — chat with the AI to change any slide, or download the PPTX and edit in PowerPoint."
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-gradient-to-br from-violet-600 to-indigo-700">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Your next deck starts with a sentence.</h2>
          <p className="text-lg text-violet-200 mb-10">No credit card. No design skills. Just describe what you need.</p>
          <a href="https://prezlab-brain.netlify.app" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-violet-700 rounded-full text-lg font-semibold hover:bg-violet-50 transition-colors shadow-lg">
            Start Free <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-gray-100">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-violet-600" />
            <span className="font-semibold">wizardly.ai</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-400">
            <span>Privacy</span>
            <span>Terms</span>
            <span>Contact</span>
          </div>
          <p className="text-sm text-gray-400">&copy; 2026 Prezlab. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

/* Sub-components */

function PainCard({ title, text, emoji }) {
  return (
    <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="text-3xl mb-4">{emoji}</div>
      <h3 className="text-lg font-bold mb-3">{title}</h3>
      <p className="text-gray-500 leading-relaxed text-sm">{text}</p>
    </div>
  );
}

function MetricCard({ value, label }) {
  return (
    <div className="bg-white rounded-xl p-6 border border-gray-100 text-center">
      <div className="text-3xl font-bold text-violet-600 mb-2">{value}</div>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  );
}

function FeatureRow({ number, title, description, icon, screenshotLabel, reverse }) {
  return (
    <div className={`flex flex-col ${reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-12`}>
      <div className="flex-1 space-y-4">
        <div className="flex items-center gap-3">
          <span className="text-sm font-mono text-violet-400">{number}</span>
          <div className="w-10 h-10 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center">{icon}</div>
        </div>
        <h3 className="text-2xl font-bold">{title}</h3>
        <p className="text-gray-500 leading-relaxed">{description}</p>
      </div>
      <div className="flex-1 w-full">
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="aspect-[4/3] flex items-center justify-center text-gray-300">
            <p className="text-sm text-gray-400 px-6 text-center">[Screenshot: {screenshotLabel}]</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StepCard({ step, title, text }) {
  return (
    <div className="flex gap-6 items-start">
      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-violet-600 text-white flex items-center justify-center font-bold text-sm">{step}</div>
      <div>
        <h3 className="text-xl font-bold mb-2">{title}</h3>
        <p className="text-gray-500 leading-relaxed">{text}</p>
      </div>
    </div>
  );
}

function UseCaseCard({ icon, role, desc }) {
  return (
    <div className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-md transition-shadow">
      <div className="w-10 h-10 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center mb-4">{icon}</div>
      <h3 className="font-bold mb-2">{role}</h3>
      <p className="text-sm text-gray-500">{desc}</p>
    </div>
  );
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors">
        <span className="font-semibold text-gray-900">{q}</span>
        <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="px-6 pb-6 text-gray-500 leading-relaxed">{a}</div>
      )}
    </div>
  );
}
